import AtAGlance from "../at-a-glance";
import imageComingSoon from "../../../assets/image-coming-soon.png";
import { HotelLocation } from "../../icons/icons";
import { AAG_ME_PARTS } from "../../../screens/rhgHotel/hotelDetailCache";
import { ICONS } from "../../../screens/presentations/partials/slide/view/atAGlanceME";
import { AAG_INDUSTRY_NAMES } from "../../../screens/presentations/partials/slide/view/aagExpandSection";

// Mirrors atAGlanceME.js: four tiles, numbers first, services filling the rest.
const MAX_STATS = 4;

/**
 * Editor preview for the "Dynamic At a glance (M&E)" layout.
 *
 * Extends AtAGlance rather than duplicating it: the hotel search, the Configure
 * modal, the media-library hooks, the offline-fallback section and the save
 * shape are all identical and stay in one place. Only the in-editor preview
 * differs, so only _renderConfigured() is overridden.
 *
 * The markup mirrors atAGlanceME.js (the slide renderer) class for class, so
 * the editor and the published slide pick up the same CSS and the author sees
 * what they will get. The five switchable panels are deliberately NOT drawn
 * here - the editor shows the slide's default (hero) state, and rendering five
 * hidden panels per block would bloat an 80-slide deck's DOM for no gain.
 */
export default class AtAGlanceME extends AtAGlance {
  static get toolbox() {
    return {
      ...AtAGlance.toolbox,
      title: "At A Glance (M&E)",
    };
  }

  /**
   * The six nav buttons, drawn flat so the author sees the real control row.
   * Inert in the editor: panel switching belongs to the published slide, and a
   * click here would fight the editor's own selection handling.
   */
  _renderNav() {
    const nav = document.createElement("nav");
    nav.classList.add("aag-me-nav");

    [
      ["Location<br/>map", AtAGlanceME.ICONS.map],
      ["How to<br/>arrive", AtAGlanceME.ICONS.arrive],
      ["Capacity<br/>chart", AtAGlanceME.ICONS.capacity],
      ["M&amp;E<br/>features", AtAGlanceME.ICONS.features],
      ["M&amp;E<br/>gallery", AtAGlanceME.ICONS.gallery],
      ["Food &amp;<br/>beverage", AtAGlanceME.ICONS.dining],
      ["Key<br/>features", AtAGlanceME.ICONS.keyfeatures],
    ].forEach(([label, icon]) => {
      const btn = document.createElement("span");
      btn.classList.add("aag-me-nav-btn");
      btn.innerHTML =
        `<span class="aag-me-nav-icon">${icon}</span>` +
        `<span class="aag-me-nav-label">${label}</span>`;
      nav.appendChild(btn);
    });

    return nav;
  }

  /**
   * Reads the room / meeting figures straight off the stored API response.
   *
   * The base block drives its capacity row from `selectedItems` (what the
   * author ticked in Configure). The M&E design has a fixed stat row instead -
   * guest rooms, meeting rooms, max capacity, breakfast - so it reads the
   * source rather than the selection, exactly as the slide renderer does.
   */
  _renderStats() {
    const api = this._data.apiData || {};
    const roomContent = api?.roomInfo?.collection?.[0]?.content || {};

    const stats = [
      [roomContent?.numOfBedrooms, "Guest<br/>rooms"],
      [roomContent?.numOfMeetingRooms, "Meeting<br/>rooms"],
      [roomContent?.maxCapacity, "Max<br/>cap"],
    ].filter(([v]) => v != null && String(v).trim() !== "");

    const hotelInfo = api?.hotel || api?.hotelInfo?.collection?.[0];
    const service = hotelInfo?.content?.service;
    const serviceList = Array.isArray(service?.values)
      ? service.values
      : service
        ? [service]
        : [];
    const serviceNames = serviceList
      .map((s) => ((s && s.service) || s)?.description || "")
      .filter(Boolean);
    const hasBreakfast = serviceNames.some((s) => /breakfast/i.test(s));

    // A hotel with none of the three figures would otherwise leave this row
    // with a lone breakfast tick, or empty. Services fill what the numbers
    // leave, matching the slide renderer and the standard At a glance.
    const fillCount = Math.max(
      0,
      MAX_STATS - stats.length - (hasBreakfast ? 1 : 0),
    );
    const fillers = serviceNames
      .filter((s) => !AAG_INDUSTRY_NAMES.includes(s) && !/breakfast/i.test(s))
      .slice(0, fillCount);

    if (!stats.length && !hasBreakfast && !fillers.length) return null;

    const row = document.createElement("div");
    row.classList.add("hotel-capacity-container", "aag-me-stats");

    const addStat = (value, labelHtml, isTick) => {
      const stat = document.createElement("div");
      stat.classList.add("hotel-capacity-group", "aag-me-stat");
      stat.innerHTML =
        `<span class="hotel-capacity-value aag-me-stat-value${
          isTick ? " aag-me-tick" : ""
        }">${isTick ? "&#10004;" : value}</span>` +
        `<span class="hotel-capacity-label aag-me-stat-label">${labelHtml}</span>`;
      row.appendChild(stat);
    };

    stats.forEach(([value, label]) => addStat(value, label, false));

    if (hasBreakfast) {
      addStat(null, "Breakfast<br/>buffet", true);
    }

    // Two short lines, same split the renderer uses.
    fillers.forEach((name) => {
      const words = String(name).trim().split(/\s+/);
      addStat(
        null,
        words.length >= 2
          ? `${words[0]}<br/>${words.slice(1).join(" ")}`
          : words[0] || "",
        true,
      );
    });

    return row;
  }

  /**
   * {@inheritdoc}
   *
   * This layout reads six of hotel-detail's properties; the base class reads
   * three.
   */
  _detailParts() {
    return AAG_ME_PARTS;
  }

  /**
   * {@inheritdoc}
   */
  _renderConfigured() {
    this._ensureHotelData();
    if (this._contentEl) {
      this._contentEl.remove();
    }

    this._contentEl = document.createElement("div");
    this._contentEl.classList.add("at-a-glance-content-container", "aag-me");
    // The published slide opens on the cover; the editor opens on the detail
    // view, because that is the state the author is configuring.
    this._contentEl.setAttribute("data-panel", "hero");

    const hotel = this._getHotelDetails();

    if (!hotel) {
      const placeholder = document.createElement("div");
      placeholder.style.cssText = "color:#999;font-size:16px;";
      placeholder.textContent = !this._data.emmaId
        ? 'Click "Configure" to set up At A Glance (M&E)'
        : this._detailError
          ? `Could not load ${this._data.emmaId}. The slide still renders - the viewer loads the hotel itself.`
          : `Loading ${this._data.emmaId}\u2026`;
      this._contentEl.appendChild(placeholder);
      this._container.appendChild(this._contentEl);
      return;
    }

    /* ── left: hero + overlay ───────────────────────────────────────────── */
    const leftCol = document.createElement("div");
    leftCol.classList.add("at-a-glance-left");

    if (hotel?.heroImage) {
      const heroImg = document.createElement("img");
      heroImg.src = hotel.heroImage;
      heroImg.classList.add("at-a-glance-hero");
      heroImg.style.opacity = this._data.heroOpacity;
      leftCol.appendChild(heroImg);
    }

    // Outside the block above: a hotel whose API record carries no hero still
    // needs the replace button, or the one image the author cannot fix is the
    // biggest one on the slide.
    if (hotel && !this.readOnly && this.config?.showMediaLibrary) {
      leftCol.appendChild(this._createReplaceBtn("hero"));
      leftCol.appendChild(this._createResetBtn("hero"));
    }

    if (hotel) {
      const overlay = document.createElement("div");
      overlay.classList.add("aag-me-hero-overlay");

      if (hotel.brandCode && this.config?.brandLogos?.[hotel.brandCode]) {
        const logoEl = document.createElement("div");
        logoEl.classList.add("hotel-logo-container");
        logoEl.innerHTML = this.config.brandLogos[hotel.brandCode];
        overlay.appendChild(logoEl);
      }

      // Same contentEditable wiring as the base block, so the title and
      // description stay editable in place and keep saving to the same fields.
      const titleEl = document.createElement("h3");
      titleEl.classList.add("aag-me-hero-title");
      titleEl.innerHTML = hotel.title || "";
      if (!this.readOnly) {
        titleEl.contentEditable = "true";
        titleEl.dataset.placeholder = "Overlay tagline";
        titleEl.addEventListener("keydown", this._handleEditableEnter);
        titleEl.addEventListener("input", () => {
          this._data.title = titleEl.innerHTML;
        });
      }
      overlay.appendChild(titleEl);

      const descEl = document.createElement("p");
      descEl.classList.add("aag-me-hero-body");
      descEl.innerHTML = hotel.description || "";
      if (!this.readOnly) {
        descEl.contentEditable = "true";
        descEl.dataset.placeholder = "Overlay description";
        descEl.addEventListener("keydown", this._handleEditableEnter);
        descEl.addEventListener("input", () => {
          this._data.description = descEl.innerHTML;
        });
      }
      overlay.appendChild(descEl);

      leftCol.appendChild(overlay);
    }

    this._contentEl.appendChild(leftCol);

    /* ── right: two images, stats, place, nav ───────────────────────────── */
    const rightCol = document.createElement("div");
    rightCol.classList.add("at-a-glance-right");

    if (hotel) {
      const canEditImages = !this.readOnly && this.config?.showMediaLibrary;
      const grid = document.createElement("div");
      grid.classList.add("aag-me-images");

      // Two slots, not the base block's four: the M&E design shows a guest
      // room and a meeting room. Empty slots still render in edit mode so the
      // author has somewhere to drop an image.
      ["guestRoom", "meetingRoom"].forEach((slot) => {
        const url = hotel.images?.[slot];
        if (!url && !canEditImages) return;

        const container = document.createElement("div");
        container.classList.add("aag-me-img");

        const img = document.createElement("img");
        if (url) {
          img.src = url;
        } else {
          container.classList.add("aag-me-img-empty");
          img.src = imageComingSoon;
        }
        img.alt = slot;
        container.appendChild(img);

        if (canEditImages) {
          container.appendChild(this._createReplaceBtn(slot));
          container.appendChild(this._createResetBtn(slot));
        }
        grid.appendChild(container);
      });

      if (grid.childElementCount) {
        rightCol.appendChild(grid);
      }

      const stats = this._renderStats();
      if (stats) rightCol.appendChild(stats);

      const place = document.createElement("div");
      place.classList.add("hotel-heading-container", "aag-me-place");
      const pin = document.createElement("div");
      pin.classList.add("location-pin-icon", "aag-me-pin");
      pin.innerHTML = HotelLocation;
      place.appendChild(pin);
      if (hotel.country) {
        const el = document.createElement("p");
        el.classList.add("hotel-country", "aag-me-country");
        el.textContent = hotel.country;
        place.appendChild(el);
      }
      if (hotel.city) {
        const el = document.createElement("h3");
        el.classList.add("hotel-city", "aag-me-city");
        el.textContent = hotel.city;
        place.appendChild(el);
      }
      if (hotel.name) {
        const el = document.createElement("p");
        el.classList.add("hotel-name", "aag-me-hotel");
        el.textContent = hotel.name;
        place.appendChild(el);
      }
      if (hotel.url) {
        const link = document.createElement("a");
        link.classList.add("aag-me-website");
        link.textContent = "Hotel website";
        link.href = "https://www.radissonhotels.com" + hotel.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        place.appendChild(link);
      }
      if (hotel.location) {
        const row = document.createElement("p");
        row.classList.add("hotel-footer-location-container", "aag-me-location");
        row.innerHTML =
          '<span class="hotel-footer-location-label aag-me-location-label">Location:</span>' +
          `<span class="hotel-footer-location aag-me-location-value">${hotel.location}</span>`;
        place.appendChild(row);
      }
      // The pin is always present, so count the rest before appending.
      if (place.childElementCount > 1) rightCol.appendChild(place);

      rightCol.appendChild(this._renderNav());
    }

    this._contentEl.appendChild(rightCol);
    this._container.appendChild(this._contentEl);
  }
}

// The slide renderer's own set, not a copy: a second one drifted out of step
// (it never gained the "How to arrive" tab) and the editor silently showed a
// different nav from the slide.
AtAGlanceME.ICONS = ICONS;
